<template>
  <div class="card-list">
    <div class="card-list__wrap">
      <ExcursionCardListSkeleton>
        <template
          v-for="(exp, index) in expList"
          :key="exp.id"
        >
          <ExcursionCardSchemaOrgWrapper
            :exp="exp"
            :is-show-schema-org="true"
          >
            <ExpCard
              :exp="exp"
              :exp-snippet-index="index"
              :context-geo-subject="contextGeoSubject"
              :show-type="showType"
              :is-user-admin="userStore.isAdmin"
              :target-blank="isTargetBlank"
              :type-label="undefined"
              :sogu-block="soguBlock"
              sogu-page="listing"
              :additional-sogu-params="listingCardSoguParams"
              class="exp-snippet"
              :allow-show-promotion-badge="true"
              :tooltip-teleport-target="targetModalElem"
              :is-in-favorites="isFavorite(exp.id)"
              is-show-favorite-btn
              :isOptimizedImage="getIsOptimizedImage(index)"
              :labels="getVisibleListingLabels(exp.labels)"
              :showMiniDiscount="isExpCardMiniDiscountActive"
              @pointerdown.capture="saveSourceRubric(exp, $event)"
              @click.capture="saveSourceRubric(exp, $event)"
              @contextmenu.capture="saveSourceRubric(exp, $event)"
              @toggle-favorite="toggleFavoriteHandle(exp)"
              @right-clicked="handleRightClick($event, exp)"
            />
          </ExcursionCardSchemaOrgWrapper>
          <slot :index="index" />
        </template>
      </ExcursionCardListSkeleton>
    </div>
  </div>
</template>

<script setup lang="ts">
  // Widgets
  import ExcursionCardListSkeleton from './ExcursionCardListSkeleton.vue';
  import ExcursionCardSchemaOrgWrapper from '@/widgets/allCardsBlock/ui/excursion/ExcursionCardSchemaOrgWrapper.vue';

  // Features
  import { useToggleExperienceFavorite } from '@/features/experience-favorite';
  import { useExcursionExperienceFavoriteSogu } from '@/features/experience-favorite/composables/excursion/useExcursionExperienceFavoriteSogu.ts';
  import { useListingLabelsV4Experiment } from '@/features/experiments';

  // Entities
  import { useUserStore } from '@/entities/user';
  import { mapExperienceDataToSogu } from '@/entities/experience/sogu';
  import { useExperienceFavorites } from '@/entities/experience/favorite';
  import { trackEvent } from '@/entities/sogu';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';

  // Shared
  import { setExperienceSourceRubricCookie } from '@/shared/lib/experienceSourceRubricCookie';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import type { RecommendationListingSoguParams } from '@/shared/types/recommendationListingSogu';

  // External and shared packages
  import { ExpCard } from '@experience/exp-card';
  import type { Experience } from '@experience/exp-card/dist/types/experience';
  import { useScreenSize } from '@experience/shared';
  import { computed, ref, type useShadowRoot } from 'vue';
  import { useExpCardMiniDiscount } from '@/features/experiments/composables/useExpCardMiniDiscount';

  interface Props {
    expList: Experience[];
    contextGeoSubject: ContextGeoSubject;
    showType: boolean;
    soguBlock?: string;
    recommendationListingSoguParams?: RecommendationListingSoguParams;
  }

  const props = withDefaults(defineProps<Props>(), {
    soguBlock: 'experiences',
  });

  const userStore = useUserStore();

  const { isMobile, isTablet } = useScreenSize();
  const { isFavorite } = useExperienceFavorites();
  const { trackExperiencesFavoriteClick } = useExcursionExperienceFavoriteSogu();
  const { toggle } = useToggleExperienceFavorite();
  const filtersStore = useExcursionFiltersStore();
  const { getVisibleListingLabels } = useListingLabelsV4Experiment();

  const isTargetBlank = computed(() => !isMobile.value && !isTablet.value);

  const getIsOptimizedImage = (index: number) => {
    if (isMobile.value) {
      return index === 0;
    }

    return [0, 1].includes(index);
  };

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);
  const modalTarget = ref<HTMLElement | null>(null);

  if (rootNode) {
    modalTarget.value = rootNode.querySelector('.modal-target');
  }

  const targetModalElem = computed(() => modalTarget.value || document.body);

  const getNormalizedPathname = (url: string) => {
    return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
  };

  const isExperienceNavigationEvent = (exp: Experience, event: Event) => {
    if (!(event.target instanceof Element)) {
      return false;
    }

    const link = event.target.closest<HTMLAnchorElement>('a[href]');

    if (!link) {
      return false;
    }

    return getNormalizedPathname(link.href) === getNormalizedPathname(exp.url);
  };

  const saveSourceRubric = (exp: Experience, event: Event) => {
    if (!isExperienceNavigationEvent(exp, event)) {
      return;
    }

    setExperienceSourceRubricCookie(exp.id, filtersStore.state.tag__slugs);
  };

  const listingCardSoguParams = computed(() => props.recommendationListingSoguParams ?? {});

  const handleRightClick = (clickType: string, slide: Experience) => {
    const payload = {
      clickType,
      ...props.contextGeoSubject.soguStamp,
      ...mapExperienceDataToSogu(slide),
    };
    trackEvent('listing:experiences:experience.right_click', {
      ...payload,
    });
  };

  // Сохраняет SOGU-клик и передаёт полный объект экскурсии в единый favorite toggle.
  const toggleFavoriteHandle = (exp: Experience) => {
    trackExperiencesFavoriteClick(exp, props.contextGeoSubject);
    toggle(exp);
  };

  const { isExpCardMiniDiscountActive } = useExpCardMiniDiscount();
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  :has(.paused-output) .card-list {
    margin-top: 32px;
  }

  .card-list__wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    @supports (display: grid) {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 40px 24px;

      @include adaptivity.tablet {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      @include adaptivity.mobile {
        grid-template-columns: minmax(0, 1fr);
        gap: 26px 24px;
      }

      @media only screen and (min-width: 1024px) and (max-width: 1075px) {
        grid-template-columns: repeat(auto-fit, minmax(300px, 0.333fr));
      }
    }
  }
</style>
